import { discoveryApiRef, fetchApiRef, useApi } from "@backstage/core-plugin-api";
import { Button, Text } from "@backstage/ui";
import {
	AVD_PLUGIN_ID,
	claudeCodeBlock,
	claudeCodeSettings,
	swaAppEnvPath,
	swaVisitorPasswordPath,
	type SwaBundle,
} from "@internal/backstage-plugin-avd-common";
import { makeStyles } from "@material-ui/core/styles";
import { useCallback, useState } from "react";

type Phase = "idle" | "busy" | "failed";

/** Reads one owner-only secret route; the value goes to the caller's handler and nowhere else. */
function useSecretRoute() {
	const discoveryApi = useApi(discoveryApiRef);
	const fetchApi = useApi(fetchApiRef);
	return useCallback(
		async (path: string) => {
			const baseUrl = await discoveryApi.getBaseUrl(AVD_PLUGIN_ID);
			const res = await fetchApi.fetch(`${baseUrl}${path}`);
			if (!res.ok) throw new Error(`answered ${res.status}`);
			return res;
		},
		[discoveryApi, fetchApi],
	);
}

/** Saves the bundle's `.env` as a file: the text is never put in state, the page or the clipboard. */
export function useEnvDownload(projectNumber: string) {
	const read = useSecretRoute();
	const [phase, setPhase] = useState<Phase>("idle");
	const download = useCallback(async () => {
		setPhase("busy");
		try {
			const blob = await (await read(swaAppEnvPath(projectNumber))).blob();
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = ".env";
			link.click();
			URL.revokeObjectURL(url);
			setPhase("idle");
		} catch {
			setPhase("failed");
		}
	}, [read, projectNumber]);
	return { phase, download };
}

/** The visitor password, held only while shown: hiding or leaving the page drops it. */
export function useVisitorPassword(projectNumber: string) {
	const read = useSecretRoute();
	const [phase, setPhase] = useState<Phase>("idle");
	const [password, setPassword] = useState<string>();
	const reveal = useCallback(async () => {
		setPhase("busy");
		try {
			const body = (await (await read(swaVisitorPasswordPath(projectNumber))).json()) as {
				visitorPassword?: string;
			};
			setPassword(body.visitorPassword);
			setPhase("idle");
		} catch {
			setPhase("failed");
		}
	}, [read, projectNumber]);
	return { phase, password, reveal, hide: () => setPassword(undefined) };
}

const useStyles = makeStyles((theme) => ({
	actions: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1) },
	group: { display: "grid", gap: theme.spacing(0.75), justifyItems: "start" },
	secret: { overflowWrap: "anywhere", fontFamily: "monospace" },
}));

/** Copies text; the button then says which copy succeeded or failed, and a screen reader announces it. */
function CopyButton({
	label,
	text,
	variant = "secondary",
}: {
	label: string;
	text: string;
	variant?: "secondary" | "tertiary";
}) {
	const [state, setState] = useState<"idle" | "copied" | "failed">("idle");
	return (
		<Button
			variant={variant}
			aria-live="polite"
			onPress={() => {
				void (async () => {
					try {
						await navigator.clipboard.writeText(text);
						setState("copied");
					} catch {
						setState("failed");
					}
				})();
			}}
		>
			{state === "copied"
				? `${label}: copied`
				: state === "failed"
					? `${label}: could not copy`
					: label}
		</Button>
	);
}

/** What a colleague with a ready web app does to connect: files and text come from the owner-only routes and the registry. */
export function ConnectActions({ bundle }: { bundle: SwaBundle }) {
	const classes = useStyles();
	const env = useEnvDownload(bundle.projectNumber);
	const visitor = useVisitorPassword(bundle.projectNumber);
	const block = claudeCodeBlock(bundle);
	const { settingsJson, gitignoreLine } = claudeCodeSettings();

	return (
		<div className={classes.group}>
			<Text as="p" variant="body-small" color="secondary">
				Connect with Claude Code without pasting a secret: download .env into your project,
				add the settings, then paste the block.
			</Text>
			{/* The two things you need to start (the file, the prompt) are secondary; the rest is tertiary. */}
			<div className={classes.actions}>
				<Button
					variant="secondary"
					isDisabled={env.phase === "busy"}
					onPress={() => void env.download()}
				>
					Download .env
				</Button>
				{block ? (
					<CopyButton label="Copy for Claude Code" text={block} />
				) : (
					<Button variant="secondary" isDisabled>
						Copy for Claude Code
					</Button>
				)}
			</div>
			<div className={classes.actions}>
				<CopyButton
					variant="tertiary"
					label="Copy Claude Code settings"
					text={`.claude/settings.json:\n${settingsJson}\n.gitignore:\n${gitignoreLine}\n`}
				/>
				{visitor.password === undefined ? (
					<Button
						variant="tertiary"
						isDisabled={visitor.phase === "busy"}
						onPress={() => void visitor.reveal()}
					>
						Reveal visitor password
					</Button>
				) : (
					<Button variant="tertiary" onPress={visitor.hide}>
						Hide visitor password
					</Button>
				)}
			</div>
			{visitor.password !== undefined && (
				<Text as="p" variant="body-small" className={classes.secret}>
					{visitor.password}
				</Text>
			)}
			{(env.phase === "failed" || visitor.phase === "failed") && (
				<Text as="p" variant="body-small" role="alert">
					Could not read it from the vault just now. Try again in a moment.
				</Text>
			)}
			{!block && (
				<Text as="p" variant="body-small" color="secondary">
					The block needs this web app's backend name, which its record does not hold.
				</Text>
			)}
			<Text as="p" variant="body-small" color="secondary">
				The settings stop Claude Code from reading .env with its own tools and the shell
				commands it recognises. That is defence in depth, not a guarantee.
			</Text>
		</div>
	);
}
